import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, ArrowLeft, Lock, Settings2, PackageX } from "lucide-react";

// reason: "role" (default) | "module_off" (optional/premium module not enabled) | "included_off" (Core Standard module
// turned off for the business) | "needs_activation" (Core Standard module awaiting Owner activation).
// Wording only — the backend enforces every role and module rule.
const COPY = {
  role: {
    icon: ShieldAlert, title: "Access Restricted",
    body: (m) => `You do not have permission to access ${m}.`,
    hint: () => "Please contact a Manager or Business Owner if access is required.",
  },
  module_off: {
    icon: PackageX, title: "Module Not Enabled",
    body: (m) => `${m} is an optional module that is not enabled for this business.`,
    hint: () => "Optional modules are added to your subscription by NEEC POS. Contact NEEC POS support if your business needs it.",
  },
  included_off: {
    icon: Lock, title: "Feature Turned Off",
    body: (m) => `${m} is included in your Standard plan but is currently turned off for this business.`,
    hint: () => "Contact NEEC POS support to turn it back on. Existing records remain available.",
  },
  needs_activation: {
    icon: Settings2, title: "Activation Required",
    body: (m) => `${m} is included in your Standard plan but has not been activated yet.`,
    hint: (role) => role === "owner"
      ? "Activate it from Settings → Customers & Credit when your business is ready. There is no additional module charge."
      : "Ask your Business Owner to activate it from Settings → Customers & Credit.",
  },
  manager_reports_off: {
    icon: Lock, title: "Reports Access Turned Off",
    body: () => "The business Owner has turned off Manager access to Reports.",
    hint: (role) => role === "owner"
      ? "Turn it back on from Settings → Permissions."
      : "Ask your Business Owner to turn it back on from Settings → Permissions if you need it.",
  },
};

export default function AccessRestricted({ module, reason = "role" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const c = COPY[reason] || COPY.role;
  const Icon = c.icon;
  const name = module || "this page";
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="access-restricted" data-reason={reason}>
      <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-500/15 text-amber-500 flex items-center justify-center mb-5">
          <Icon className="w-7 h-7" />
        </div>
        <h1 className="font-head text-2xl font-extrabold" data-testid="access-restricted-title">{c.title}</h1>
        <p className="text-sm text-muted-foreground mt-2" data-testid="access-restricted-body">{c.body(name)}</p>
        <p className="text-sm text-muted-foreground mt-1" data-testid="access-restricted-hint">{c.hint(user?.role)}</p>
        <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
          {reason === "needs_activation" && user?.role === "owner" && (
            <Button onClick={() => nav("/settings?tab=customers")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="access-restricted-activate">
              <Settings2 className="w-4 h-4 mr-1.5" /> Go to Customers &amp; Credit
            </Button>
          )}
          {reason === "manager_reports_off" && user?.role === "owner" && (
            <Button onClick={() => nav("/settings?tab=permissions")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="access-restricted-permissions">
              <Settings2 className="w-4 h-4 mr-1.5" /> Go to Permissions
            </Button>
          )}
          <Button variant={(reason === "needs_activation" || reason === "manager_reports_off") && user?.role === "owner" ? "outline" : "default"} onClick={() => nav("/pos")}
            className={(reason === "needs_activation" || reason === "manager_reports_off") && user?.role === "owner" ? "" : "bg-emerald-600 hover:bg-emerald-700 text-white"} data-testid="access-restricted-return">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Return to POS
          </Button>
        </div>
      </div>
    </div>
  );
}
